import { KPI_TEXT_SHADOW } from '@/components/ui/KpiCard';
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, PartyPopper, type LucideIcon } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/form/Select';
import { SearchableSelect, type SearchableOption } from '@/components/form/SearchableSelect';
import {
  listSisExceptions,
  getSisExceptionsSummary,
  resolveSisException,
  listSisStores,
  type SisException,
} from '@/api/sisStock';
import { formatDate } from '@/lib/format';

const SEVERITY_TONE: Record<string, 'danger' | 'warning'> = {
  Error: 'danger',
  Warning: 'warning',
};

export function SisExceptionsPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('Open');
  const [category, setCategory] = useState('');
  const [partyId, setPartyId] = useState('');

  const filter = useMemo(
    () => ({
      status: status || undefined,
      category: category || undefined,
      partyId: partyId ? Number(partyId) : undefined,
    }),
    [status, category, partyId],
  );

  const storesQ = useQuery({ queryKey: ['sis-stores'], queryFn: () => listSisStores(), staleTime: 0 });
  const summaryQ = useQuery({
    queryKey: ['sis-exceptions-summary'],
    queryFn: () => getSisExceptionsSummary(),
    refetchInterval: 60_000,
  });
  const query = useQuery({
    queryKey: ['sis-exceptions', filter],
    queryFn: () => listSisExceptions(filter),
    refetchInterval: 60_000,
  });
  const rows = query.data ?? [];

  const resolveM = useMutation({
    mutationFn: (id: string) => resolveSisException(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sis-exceptions'] });
      qc.invalidateQueries({ queryKey: ['sis-exceptions-summary'] });
    },
  });

  const summary = summaryQ.data;

  return (
    <div>
      <SectionHeader
        title="SIS Exceptions"      />

      {/* Headline counts — ERP KPI cards */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="Open" value={summary?.open} accent="#ef4444" Icon={AlertTriangle} loading={summaryQ.isLoading} />
        <KpiCard label="Resolved" value={summary?.resolved} accent="#10b981" Icon={CheckCircle2} loading={summaryQ.isLoading} />
        <div
          className="col-span-2 flex flex-wrap items-center gap-1.5 rounded-xl p-4"
          style={{ border: '1px solid var(--border-color)', background: 'var(--bg-card)' }}
        >
          {summary && summary.byCategory.length > 0 ? (
            summary.byCategory.map((c) => (
              <button
                key={c.category}
                onClick={() => setCategory(c.category === category ? '' : c.category)}
                title={`Filter by ${c.category}`}
              >
                <Badge tone={c.category === category ? 'accent' : 'muted'}>
                  {c.category} · {c.count}
                </Badge>
              </button>
            ))
          ) : (
            <span className="text-xs text-[var(--text-muted)]">No open exceptions <PartyPopper size={13} className="ml-1 inline-block align-[-2px]" /></span>
          )}
        </div>
      </div>

      <FilterBar>
        <FilterField label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option>Open</option>
            <option>Resolved</option>
          </Select>
        </FilterField>
        <FilterField label="Category">
          <Select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All categories</option>
            <option>Unknown Store</option>
            <option>Unknown SKU</option>
            <option>Duplicate Txn</option>
            <option>Oversold</option>
            <option>Not Stocked</option>
            <option>Bad Data</option>
            <option>Price &gt; MRP</option>
            <option>Low Stock</option>
          </Select>
        </FilterField>
        <FilterField label="Store">
          <SearchableSelect
            options={[
              { value: '', label: 'All Stores' },
              ...(storesQ.data ?? []).map((s) => ({
                value: String(s.id),
                label: s.name,
                sublabel: s.city || undefined,
              })),
            ] as SearchableOption[]}
            value={partyId}
            onChange={(v) => setPartyId(v === '' ? '' : String(v))}
            placeholder="Type a store name…"
          />
        </FilterField>
      </FilterBar>

      <Table<SisException>
        storageKey="sis-exceptions"
        paginated
        title="Exceptions"
        count={`${rows.length} rows`}
        rows={rows}
        rowKey={(r) => r.id}
        loading={query.isFetching && rows.length === 0}
        columns={[
          { key: 'date', header: 'Raised', width: '110px', cell: (r) => formatDate(r.raisedAt) },
          {
            key: 'sev',
            header: 'Severity',
            width: '110px',
            align: 'center',
            cell: (r) => <Badge tone={SEVERITY_TONE[r.severity] ?? 'muted'}>{r.severity}</Badge>,
          },
          { key: 'cat', header: 'Category', width: '130px', align: 'center', cell: (r) => <Badge tone="muted">{r.category}</Badge> },
          { key: 'store', header: 'Store', width: '170px', cell: (r) => <span className="font-bold">{r.storeName || '—'}</span> },
          {
            key: 'item',
            header: 'Item',
            minWidth: '220px',
            cell: (r) => (r.itemCode ? (r.itemName && r.itemName !== r.itemCode ? `${r.itemCode} · ${r.itemName}` : r.itemCode) : '—'),
          },
          { key: 'ref', header: 'Ref', width: '160px', cell: (r) => <span className="font-mono text-xs text-[var(--text-muted)]">{r.rowRef ?? '—'}</span> },
          { key: 'msg', header: 'Message', cell: (r) => r.message },
          {
            key: 'status',
            header: 'Status',
            width: '100px',
            align: 'center',
            cell: (r) =>
              r.status === 'Resolved' ? (
                <Badge tone="success">Resolved</Badge>
              ) : (
                <Badge tone="warning">Open</Badge>
              ),
          },
          {
            key: 'action',
            header: '',
            width: '110px',
            align: 'right',
            cell: (r) =>
              r.status === 'Open' ? (
                <Button
                  size="sm"
                  variant="glass"
                  disabled={resolveM.isPending}
                  onClick={() => resolveM.mutate(r.id)}
                >
                  Resolve
                </Button>
              ) : (
                <span className="text-xs text-[var(--text-muted)]">{r.resolvedBy ?? ''}</span>
              ),
          },
        ]}
      />
    </div>
  );
}

function KpiCard({
  label,
  value,
  accent,
  Icon,
  loading,
}: {
  label: string;
  value: number | undefined;
  accent: string;
  Icon: LucideIcon;
  loading: boolean;
}) {
  const val = value ?? null;
  return (
    <div
      className="relative overflow-hidden rounded-xl p-4 text-white shadow-sm transition-transform hover:-translate-y-0.5"
      style={{ background: accent, textShadow: KPI_TEXT_SHADOW }}
    >
      <Icon className="absolute -bottom-3 -right-3 h-20 w-20" style={{ color: '#fff', opacity: 0.18 }} strokeWidth={1.25} />
      <div className="relative">
        <span className="text-[10px] font-extrabold uppercase tracking-widest text-white/90">
          {label}
        </span>
        <div className={`mt-2 text-2xl font-extrabold ${val === null ? 'text-white/70' : ''}`}>
          {val === null
            ? loading
              ? <span className="inline-block h-6 w-12 animate-pulse rounded" style={{ background: 'rgba(255,255,255,0.3)' }} />
              : '—'
            : val}
        </div>
      </div>
    </div>
  );
}
